import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { DataStateChangeEvent, RowClassArgs } from '@progress/kendo-angular-grid';
import { filterBy, State } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import { TranslateService } from 'ng2-translate/ng2-translate';

import { ArrayType, ConfigService } from '../../../../core/config.service';
import { DistributionColor } from '../../../domain/enums/distribution-color';
import { DistributionStatus } from '../../../domain/enums/distribution-status';
import { UserRole } from '../../../domain/enums/user-role';
import { WeekOfYear } from '../../../domain/models/week-of-year';
import Constants from '../../../shared/constants';
import { DistributionListService } from '../../services/distribution-list.service';
import { DistributionTiming } from '../../../domain/enums/distribution-timing';
import { DistributionListItemViewModel } from '../../view-models/distribution-list-item.view-model';

@Component({
    selector: 'app-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ListComponent implements OnInit {

    @Input() activeWeek: WeekOfYear;
    @Input() items: DistributionListItemViewModel[];
    @Input() loading: boolean;
    @Input() weeks: WeekOfYear[];

    @Output() onWeekChange: EventEmitter<WeekOfYear> = new EventEmitter();
    @Output() onRowClick: EventEmitter<DistributionListItemViewModel> = new EventEmitter();
    @Output() onDetailClick: EventEmitter<DistributionListItemViewModel> = new EventEmitter();
    @Output() onDistributionsFilter: EventEmitter<string[]> = new EventEmitter();

    private gridOverflowClass: string;
    private currentUserRoles: UserRole[];
    private defaultPageSize: number;
    public pageSizes: number[];
    public state: State;

    constructor(
        private distributionService: DistributionListService,
        private translate: TranslateService,
        private configService: ConfigService,
        private translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.currentUserRoles = this.distributionService.getCurrentUserRoles();
        this.setGridOverflow(0);
        this.defaultPageSize = parseInt(this.configService.getEnv('gridDefaultPageSize'), 10);
        this.pageSizes = this.configService.getArray('gridPossiblePageSizes', ArrayType.Number);
        this.state = {
            skip: 0,
            take: this.defaultPageSize
        };
    }

    public cellCallback(event: any): void {
        const dataItem: DistributionListItemViewModel = event.dataItem;
        this.onRowClick.emit(dataItem);
    }

    public weekChange(value: WeekOfYear): void {
        if (value) {
            this.onWeekChange.emit(value);
            this.state.skip = 0;
            this.setGridOverflow(0);
        }
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        this.state = state;
    }

    public onFilter(event: any): void {
        const filteredData: DistributionListItemViewModel[] = filterBy(this.items, event);
        const filteredSectorIds: string[] = filteredData.map((x: DistributionListItemViewModel) => x.distributionId);
        this.onDistributionsFilter.emit(filteredSectorIds);
    }

    private setGridOverflow(timeout: number): void {
        setTimeout(() => {
            const gridNoOverFlow: string = 'grid-no-overflow';
            let grid: HTMLCollection = document.getElementsByClassName('k-grid-content k-virtual-content');
            if (!grid || grid.length === 0) {
                this.gridOverflowClass = gridNoOverFlow;
            }
            const gridContent: Element = grid[0];
            this.gridOverflowClass = (gridContent && gridContent.scrollHeight > gridContent.clientHeight) ? '' : gridNoOverFlow;
        }, timeout);
    }

    public hasDistributionStatusString(distributionStatus: DistributionStatus): boolean {
        return Constants.DistributionStatusStrings.get(distributionStatus).length > 0;
    }

    public getDistributionStatusString(distributionStatus: DistributionStatus): string {
        const stateString: string = Constants.DistributionStatusStrings.get(distributionStatus);
        if (stateString.length > 0) {
            return this.translate.instant(Constants.DistributionStatusStrings.get(distributionStatus));
        }
        return '';
    }

    public rowCallback(context: RowClassArgs): any {
        const isEven: boolean = context.index % 2 === 0;
        return {
            even: isEven,
            odd: !isEven
        };
    }

    public getKendoDateTimeFormat(): string {
        return Constants.KendoDateTimeFormat;
    }

    public batteryEmptyOrUnknown(battery: number): boolean {
        return battery === null || battery === undefined || battery < 10;
    }

    public batteryQuarter(battery: number): boolean {
        return battery >= 10 && battery < 40;
    }

    public batteryHalf(battery: number): boolean {
        return battery >= 40 && battery < 60;
    }

    public batteryThreeQuarters(battery: number): boolean {
        return battery >= 60 && battery < 90;
    }

    public batteryFull(battery: number): boolean {
        return battery >= 90;
    }

    public getBatteryColor(battery: number): string {
        if (battery >= 70) {
            return Constants.StatusColors.Green;
        }
        if (battery >= 20 && battery < 70) {
            return Constants.StatusColors.Orange;
        }
        return Constants.StatusColors.Red;
    }

    public getDistributionProgressColor(distributionProgress: number): string {
        if (distributionProgress === 100) {
            return DistributionColor.DarkGreen;
        }
        if (distributionProgress >= 95 && distributionProgress < 100) {
            return DistributionColor.Green;
        }
        if (distributionProgress >= 80 && distributionProgress < 95) {
            return DistributionColor.Yellow;
        }
        if (distributionProgress > 10 && distributionProgress < 80) {
            return DistributionColor.Orange;
        }
        return DistributionColor.Red;
    }

    public getGridOverflowClass(): string {
        return this.gridOverflowClass;
    }

    public onResizeWindow(): void {
        this.setGridOverflow(100);
    }

    public isTeamManagerOrHigher(): boolean {
        return _.some(this.currentUserRoles, (role: UserRole) => role >= UserRole.TeamManager);
    }

    public goToDetail(distribution: DistributionListItemViewModel): void {
        this.onDetailClick.emit(distribution);
    }

    public getDistributionTimingDisplayValue(distributionTiming: DistributionTiming): string {
        return Constants.DistributionTimingClass.get(distributionTiming);
    }

    public getTranslatedTiming(timing: string): string {
        return this.translateService.instant(timing);
    }

    public getHref(distribution: DistributionListItemViewModel): string {
        return 'distributions/' + distribution.distributionId;
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/distribution/presentation/list/list.component.ts